import { api } from "@/redux/api";

import {
	AiTestRequest,
	AiTestResponse,
	AiTestResponseSchema,
	BulkCreateMsdynamicsFieldMappings,
	CreateMsdynamicsDealRequest,
	CreateMsdynamicsDealResponse,
	CreateMsdynamicsDealResponseSchema,
	CreateMsdynamicsFieldMapping,
	CreateMsdynamicsUserMapping,
	MsdynamicsCompanySyncTriggerResponse,
	MsdynamicsCompanySyncTriggerResponseSchema,
	MsdynamicsConversationSyncResponse,
	MsdynamicsConversationSyncResponseSchema,
	MsdynamicsMarketingConfig,
	MsdynamicsMarketingConfigSchema,
	MsdynamicsDealConfigResponse,
	MsdynamicsDealConfigResponseSchema,
	MsdynamicsDealCreationTriggerResponse,
	MsdynamicsDealCreationTriggerResponseSchema,
	MsdynamicsEntityType,
	MsdynamicsFieldMapping,
	MsdynamicsFieldMappingsResponse,
	MsdynamicsFieldMappingsResponseSchema,
	MsdynamicsFieldsResponse,
	MsdynamicsFieldsResponseSchema,
	MsdynamicsLeadConfigResponse,
	MsdynamicsLeadConfigResponseSchema,
	MsdynamicsListSyncTriggerResponse,
	MsdynamicsListSyncTriggerResponseSchema,
	MsdynamicsOAuthConnectResponse,
	MsdynamicsOAuthConnectResponseSchema,
	MsdynamicsOAuthDisconnectResponse,
	MsdynamicsOAuthDisconnectResponseSchema,
	MsdynamicsOAuthStatusResponse,
	MsdynamicsOAuthStatusResponseSchema,
	MsdynamicsOwnersResponse,
	MsdynamicsOwnersResponseSchema,
	MsdynamicsPipelinesResponse,
	MsdynamicsPipelinesResponseSchema,
	MsdynamicsResyncResponse,
	MsdynamicsResyncResponseSchema,
	MsdynamicsSourceFieldsResponse,
	MsdynamicsSourceFieldsResponseSchema,
	MsdynamicsSyncLogsResponse,
	MsdynamicsSyncLogsResponseSchema,
	MsdynamicsUserMapping,
	MsdynamicsUserMappingsResponse,
	MsdynamicsUserMappingsResponseSchema,
	UpdateMsdynamicsCompanySyncTrigger,
	UpdateMsdynamicsConversationSync,
	UpdateMsdynamicsMarketingConfig,
	UpdateMsdynamicsDealConfig,
	UpdateMsdynamicsDealCreationTrigger,
	UpdateMsdynamicsFieldMapping,
	UpdateMsdynamicsLeadConfig,
	UpdateMsdynamicsListSyncTrigger,
} from "../../pages/Company/schema/dynamics";
import {
	MsdynamicsFieldMappingPreviewRequest,
	MsdynamicsFieldMappingPreviewResponse,
	MsdynamicsFieldMappingPreviewResponseSchema,
	MsdynamicsTestSyncRequest,
	MsdynamicsTestSyncResponse,
	MsdynamicsTestSyncResponseSchema,
} from "../../pages/Company/schema/dynamicsMappingPreview";

export const dynamicsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		// ── Attribute discovery ──────────────────────────────────────
		getDynamicsContactFields: builder.query<MsdynamicsFieldsResponse, void>({
			query: () => ({
				url: "msdynamics/fields/contacts",
				method: "GET",
				responseSchema: MsdynamicsFieldsResponseSchema,
			}),
			providesTags: ["Dynamics"],
		}),
		getDynamicsAccountFields: builder.query<MsdynamicsFieldsResponse, void>({
			query: () => ({
				url: "msdynamics/fields/accounts",
				method: "GET",
				responseSchema: MsdynamicsFieldsResponseSchema,
			}),
			providesTags: ["Dynamics"],
		}),
		getDynamicsLeadFields: builder.query<MsdynamicsFieldsResponse, void>({
			query: () => ({
				url: "msdynamics/fields/leads",
				method: "GET",
				responseSchema: MsdynamicsFieldsResponseSchema,
			}),
			providesTags: ["Dynamics"],
		}),
		getDynamicsOpportunityFields: builder.query<MsdynamicsFieldsResponse, void>({
			query: () => ({
				url: "msdynamics/fields/opportunities",
				method: "GET",
				responseSchema: MsdynamicsFieldsResponseSchema,
			}),
			providesTags: ["Dynamics"],
		}),
		getDynamicsSourceFields: builder.query<MsdynamicsSourceFieldsResponse, void>({
			query: () => ({
				url: "msdynamics/source-fields",
				method: "GET",
				responseSchema: MsdynamicsSourceFieldsResponseSchema,
			}),
			providesTags: ["Dynamics"],
		}),

		// ── Field mapping CRUD ───────────────────────────────────────
		getDynamicsFieldMappings: builder.query<
			MsdynamicsFieldMappingsResponse,
			{ entityType?: MsdynamicsEntityType }
		>({
			query: (params) => ({
				url: "msdynamics/field-mappings",
				method: "GET",
				params,
				responseSchema: MsdynamicsFieldMappingsResponseSchema,
			}),
			providesTags: ["Dynamics"],
		}),
		createDynamicsFieldMapping: builder.mutation<
			MsdynamicsFieldMapping,
			CreateMsdynamicsFieldMapping
		>({
			query: (body) => ({ url: "msdynamics/field-mappings", method: "POST", body }),
			invalidatesTags: ["Dynamics"],
		}),
		updateDynamicsFieldMapping: builder.mutation<
			MsdynamicsFieldMapping,
			{ id: number; data: UpdateMsdynamicsFieldMapping }
		>({
			query: ({ id, data }) => ({
				url: `msdynamics/field-mappings/${id}`,
				method: "PUT",
				body: data,
			}),
			invalidatesTags: ["Dynamics"],
		}),
		deleteDynamicsFieldMapping: builder.mutation<{ success: boolean }, number>({
			query: (id) => ({ url: `msdynamics/field-mappings/${id}`, method: "DELETE" }),
			invalidatesTags: ["Dynamics"],
		}),
		bulkCreateDynamicsFieldMappings: builder.mutation<
			MsdynamicsFieldMappingsResponse,
			BulkCreateMsdynamicsFieldMappings
		>({
			query: (body) => ({ url: "msdynamics/field-mappings/bulk", method: "POST", body }),
			invalidatesTags: ["Dynamics"],
		}),
		/**
		 * Adds the out-of-the-box mappings the company is missing (a fresh connection is
		 * seeded with the same set). Returns only the created rows, so an empty array means
		 * every default is already configured.
		 */
		restoreDynamicsDefaultFieldMappings: builder.mutation<
			MsdynamicsFieldMappingsResponse,
			{ entityType?: MsdynamicsEntityType }
		>({
			query: (params) => ({
				url: "msdynamics/field-mappings/defaults",
				method: "POST",
				params,
				responseSchema: MsdynamicsFieldMappingsResponseSchema,
			}),
			invalidatesTags: ["Dynamics"],
		}),
		/** Dry run: resolves the tab's mappings against one prospect without writing to Dataverse. */
		previewDynamicsFieldMappings: builder.mutation<
			MsdynamicsFieldMappingPreviewResponse,
			MsdynamicsFieldMappingPreviewRequest
		>({
			query: (body) => ({
				url: "msdynamics/field-mappings/preview",
				method: "POST",
				body,
				responseSchema: MsdynamicsFieldMappingPreviewResponseSchema,
			}),
		}),
		/** Real write: runs the actual sync for one prospect and reports Dataverse's answer. */
		testDynamicsSync: builder.mutation<MsdynamicsTestSyncResponse, MsdynamicsTestSyncRequest>({
			query: (body) => ({
				url: "msdynamics/sync/test",
				method: "POST",
				body,
				responseSchema: MsdynamicsTestSyncResponseSchema,
			}),
			invalidatesTags: ["Dynamics"],
		}),
		previewDynamicsAiVariables: builder.mutation<
			{ resolvedPrompt: string; variables: Record<string, string> },
			{ prospectId: number; prompt: string }
		>({
			query: (body) => ({
				url: "msdynamics/field-mappings/ai/preview-variables",
				method: "POST",
				body,
			}),
		}),
		testDynamicsAiFieldMapping: builder.mutation<AiTestResponse, AiTestRequest>({
			query: (body) => ({
				url: "msdynamics/field-mappings/ai/test",
				method: "POST",
				body,
				responseSchema: AiTestResponseSchema,
			}),
		}),

		// ── Sync trigger — company ───────────────────────────────────
		getDynamicsCompanySyncTrigger: builder.query<MsdynamicsCompanySyncTriggerResponse, void>({
			query: () => ({
				url: "msdynamics/sync-trigger/company",
				method: "GET",
				responseSchema: MsdynamicsCompanySyncTriggerResponseSchema,
			}),
			providesTags: ["Dynamics"],
		}),
		updateDynamicsCompanySyncTrigger: builder.mutation<
			MsdynamicsCompanySyncTriggerResponse,
			UpdateMsdynamicsCompanySyncTrigger
		>({
			query: (body) => ({ url: "msdynamics/sync-trigger/company", method: "PUT", body }),
			invalidatesTags: ["Dynamics", "Me", "Company"],
		}),

		// ── Sync trigger — list ──────────────────────────────────────
		getDynamicsListSyncTrigger: builder.query<MsdynamicsListSyncTriggerResponse, number>({
			query: (listId) => ({
				url: `msdynamics/sync-trigger/list/${listId}`,
				method: "GET",
				responseSchema: MsdynamicsListSyncTriggerResponseSchema,
			}),
			providesTags: ["Dynamics"],
		}),
		updateDynamicsListSyncTrigger: builder.mutation<
			MsdynamicsListSyncTriggerResponse,
			{ listId: number; data: UpdateMsdynamicsListSyncTrigger }
		>({
			query: ({ listId, data }) => ({
				url: `msdynamics/sync-trigger/list/${listId}`,
				method: "PUT",
				body: data,
			}),
			invalidatesTags: ["Dynamics"],
		}),

		// ── Opportunity-creation trigger (company-scoped only) ───────
		getDynamicsDealCreationTrigger: builder.query<MsdynamicsDealCreationTriggerResponse, void>({
			query: () => ({
				url: "msdynamics/deal-creation-trigger",
				method: "GET",
				responseSchema: MsdynamicsDealCreationTriggerResponseSchema,
			}),
			providesTags: ["Dynamics"],
		}),
		updateDynamicsDealCreationTrigger: builder.mutation<
			MsdynamicsDealCreationTriggerResponse,
			UpdateMsdynamicsDealCreationTrigger
		>({
			query: (body) => ({ url: "msdynamics/deal-creation-trigger", method: "PUT", body }),
			invalidatesTags: ["Dynamics", "Me", "Company"],
		}),

		// ── Pipelines & owners ───────────────────────────────────────
		getDynamicsPipelines: builder.query<MsdynamicsPipelinesResponse, void>({
			query: () => ({
				url: "msdynamics/pipelines",
				method: "GET",
				responseSchema: MsdynamicsPipelinesResponseSchema,
			}),
			providesTags: ["Dynamics"],
		}),
		getDynamicsOwners: builder.query<MsdynamicsOwnersResponse, void>({
			query: () => ({
				url: "msdynamics/owners",
				method: "GET",
				responseSchema: MsdynamicsOwnersResponseSchema,
			}),
			providesTags: ["Dynamics"],
		}),

		// ── Opportunity / lead configuration ─────────────────────────
		getDynamicsDealConfig: builder.query<MsdynamicsDealConfigResponse, void>({
			query: () => ({
				url: "msdynamics/deal-config",
				method: "GET",
				responseSchema: MsdynamicsDealConfigResponseSchema,
			}),
			providesTags: ["Dynamics"],
		}),
		saveDynamicsDealConfig: builder.mutation<
			MsdynamicsDealConfigResponse,
			UpdateMsdynamicsDealConfig
		>({
			query: (body) => ({ url: "msdynamics/deal-config", method: "PUT", body }),
			invalidatesTags: ["Dynamics"],
		}),
		getDynamicsLeadConfig: builder.query<MsdynamicsLeadConfigResponse, void>({
			query: () => ({
				url: "msdynamics/lead-config",
				method: "GET",
				responseSchema: MsdynamicsLeadConfigResponseSchema,
			}),
			providesTags: ["Dynamics"],
		}),
		saveDynamicsLeadConfig: builder.mutation<
			MsdynamicsLeadConfigResponse,
			UpdateMsdynamicsLeadConfig
		>({
			query: (body) => ({ url: "msdynamics/lead-config", method: "PUT", body }),
			invalidatesTags: ["Dynamics"],
		}),

		// ── User mappings ────────────────────────────────────────────
		getDynamicsUserMappings: builder.query<MsdynamicsUserMappingsResponse, void>({
			query: () => ({
				url: "msdynamics/user-mappings",
				method: "GET",
				responseSchema: MsdynamicsUserMappingsResponseSchema,
			}),
			providesTags: ["Dynamics"],
		}),
		createDynamicsUserMapping: builder.mutation<MsdynamicsUserMapping, CreateMsdynamicsUserMapping>(
			{
				query: (body) => ({ url: "msdynamics/user-mappings", method: "POST", body }),
				invalidatesTags: ["Dynamics"],
			}
		),
		deleteDynamicsUserMapping: builder.mutation<{ success: boolean }, number>({
			query: (id) => ({ url: `msdynamics/user-mappings/${id}`, method: "DELETE" }),
			invalidatesTags: ["Dynamics"],
		}),

		// ── Manual opportunity / lead creation ───────────────────────
		createDynamicsDeal: builder.mutation<CreateMsdynamicsDealResponse, CreateMsdynamicsDealRequest>(
			{
				query: (body) => ({
					url: "msdynamics/deals/create",
					method: "POST",
					body,
					responseSchema: CreateMsdynamicsDealResponseSchema,
				}),
				invalidatesTags: ["Dynamics"],
			}
		),

		// ── Conversation sync ────────────────────────────────────────
		getDynamicsConversationSync: builder.query<MsdynamicsConversationSyncResponse, void>({
			query: () => ({
				url: "msdynamics/conversation-sync",
				method: "GET",
				responseSchema: MsdynamicsConversationSyncResponseSchema,
			}),
			providesTags: ["Dynamics"],
		}),
		updateDynamicsConversationSync: builder.mutation<
			MsdynamicsConversationSyncResponse,
			UpdateMsdynamicsConversationSync
		>({
			query: (body) => ({ url: "msdynamics/conversation-sync", method: "PUT", body }),
			invalidatesTags: ["Dynamics"],
		}),

		// ── Marketing lists & campaigns ──────────────────────────────
		getDynamicsMarketingConfig: builder.query<MsdynamicsMarketingConfig, void>({
			query: () => ({
				url: "msdynamics/marketing-config",
				method: "GET",
				responseSchema: MsdynamicsMarketingConfigSchema,
			}),
			providesTags: ["Dynamics"],
		}),
		updateDynamicsMarketingConfig: builder.mutation<
			MsdynamicsMarketingConfig,
			UpdateMsdynamicsMarketingConfig
		>({
			query: (body) => ({ url: "msdynamics/marketing-config", method: "PUT", body }),
			invalidatesTags: ["Dynamics"],
		}),

		// ── OAuth ────────────────────────────────────────────────────
		/**
		 * Unlike every other provider this is **not** a bare link: a Dataverse token is
		 * scoped to one environment, so the customer's environment URL is required up front
		 * and the backend rejects anything that is not a bare
		 * `https://<org>.crm<digits?>.dynamics.com` origin with a 400.
		 */
		getDynamicsOAuthConnectUrl: builder.query<
			MsdynamicsOAuthConnectResponse,
			{ environmentUrl: string }
		>({
			query: ({ environmentUrl }) => ({
				url: "msdynamics/oauth/connect",
				method: "GET",
				params: { environmentUrl },
				responseSchema: MsdynamicsOAuthConnectResponseSchema,
			}),
		}),
		getDynamicsOAuthStatus: builder.query<MsdynamicsOAuthStatusResponse, void>({
			query: () => ({
				url: "msdynamics/oauth/status",
				method: "GET",
				responseSchema: MsdynamicsOAuthStatusResponseSchema,
			}),
			providesTags: ["Dynamics"],
		}),
		disconnectDynamicsOAuth: builder.mutation<MsdynamicsOAuthDisconnectResponse, void>({
			query: () => ({
				url: "msdynamics/oauth",
				method: "DELETE",
				responseSchema: MsdynamicsOAuthDisconnectResponseSchema,
			}),
			invalidatesTags: ["Dynamics", "Me", "Company"],
		}),

		// ── Manual resync & sync logs ────────────────────────────────
		triggerDynamicsResync: builder.mutation<MsdynamicsResyncResponse, void>({
			query: () => ({
				url: "msdynamics/sync/resync",
				method: "POST",
				responseSchema: MsdynamicsResyncResponseSchema,
			}),
			invalidatesTags: ["Dynamics"],
		}),
		getDynamicsSyncLogs: builder.query<
			MsdynamicsSyncLogsResponse,
			{ entityType?: string; limit?: number; offset?: number } | void
		>({
			query: (params) => ({
				url: "msdynamics/sync/logs",
				method: "GET",
				params: params || undefined,
				responseSchema: MsdynamicsSyncLogsResponseSchema,
			}),
			providesTags: ["Dynamics"],
		}),
	}),
});

export const {
	useGetDynamicsContactFieldsQuery,
	useGetDynamicsAccountFieldsQuery,
	useGetDynamicsLeadFieldsQuery,
	useGetDynamicsOpportunityFieldsQuery,
	useGetDynamicsSourceFieldsQuery,
	useGetDynamicsFieldMappingsQuery,
	useCreateDynamicsFieldMappingMutation,
	useUpdateDynamicsFieldMappingMutation,
	useDeleteDynamicsFieldMappingMutation,
	useBulkCreateDynamicsFieldMappingsMutation,
	useRestoreDynamicsDefaultFieldMappingsMutation,
	usePreviewDynamicsFieldMappingsMutation,
	useTestDynamicsSyncMutation,
	usePreviewDynamicsAiVariablesMutation,
	useTestDynamicsAiFieldMappingMutation,
	useGetDynamicsCompanySyncTriggerQuery,
	useUpdateDynamicsCompanySyncTriggerMutation,
	useGetDynamicsListSyncTriggerQuery,
	useUpdateDynamicsListSyncTriggerMutation,
	useGetDynamicsDealCreationTriggerQuery,
	useUpdateDynamicsDealCreationTriggerMutation,
	useGetDynamicsPipelinesQuery,
	useGetDynamicsOwnersQuery,
	useGetDynamicsDealConfigQuery,
	useSaveDynamicsDealConfigMutation,
	useGetDynamicsLeadConfigQuery,
	useSaveDynamicsLeadConfigMutation,
	useGetDynamicsUserMappingsQuery,
	useCreateDynamicsUserMappingMutation,
	useDeleteDynamicsUserMappingMutation,
	useCreateDynamicsDealMutation,
	useGetDynamicsConversationSyncQuery,
	useUpdateDynamicsConversationSyncMutation,
	useGetDynamicsMarketingConfigQuery,
	useUpdateDynamicsMarketingConfigMutation,
	useLazyGetDynamicsOAuthConnectUrlQuery,
	useGetDynamicsOAuthStatusQuery,
	useDisconnectDynamicsOAuthMutation,
	useTriggerDynamicsResyncMutation,
	useGetDynamicsSyncLogsQuery,
} = dynamicsApi;

export const dynamicsApiReducer = dynamicsApi.reducer;
export const dynamicsApiMiddleware = dynamicsApi.middleware;
